import SectionMessage from '@atlaskit/section-message';
import React, { useEffect } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import config from 'appconfig';
import { State } from 'model/State';
import { selectAccountHistory, selectTrackedElsewhere } from 'modules/mac/duck/selectors';
import {
  addOnProductTypes,
  atlassianCloudKeys,
  BTFProductTypes,
  hostedAccountTypes,
  isAdvisoryServicesProduct,
  isCommercialOrAcademicOrDeveloperType,
  isNoLicenseKeyValueDisplayedForKeys,
  serverIdNotRequiredForKeys,
  serverIdRequiredForProductTypes,
} from 'util/mac/helpers';
import { Dimensions } from 'components/visuals';
import {
  StyledFlex,
  StyledLink,
  StyledSenInfoWrapper,
  StyledSpan,
  StyledTableData,
  StyledTDSection,
  StyledTextArea,
  StyledTextLabel,
  StyledTextValueWrapper,
} from './StyledComponents';
import { messages } from '../../messages';
import { fetchPartnerDetails } from 'modules/mac/duck/actions/contact';
import { ATLASSIAN_ORGANISATION } from 'data/products/keys';
import { HostedAccountsDetails } from './licensesRowHostedAccountsDetails';
import ContactsSection from './licensesRowContacts';
import NonPubliclyAvailableAddOnMessage from './NonPubliclyAvailableAddOnMessage';
import ActionsSection from './ActionsSection';
import AccountHistory from './licensesRowHistory';
import { LICENSE_EVALUATION } from 'model/hams/LicenseType';

export const DetailsRow = ({ label, value, dataCy = '', width = 100, boldValue = false }) => {
  return (
    <StyledFlex {...(dataCy ? { 'data-cy': dataCy } : {})} width={`${width}%`}>
      <StyledTextLabel>{label}</StyledTextLabel>
      <StyledTextValueWrapper>
        <StyledSpan boldValue={boldValue}>{value}</StyledSpan>
      </StyledTextValueWrapper>
    </StyledFlex>
  );
};

const OrganizationLine = ({ latestPeriod }) => (
  <DetailsRow
    dataCy="action-section-organization-line"
    label={<FormattedMessage id="mac.licenses.organization" defaultMessage="Organization" />}
    value={latestPeriod.license?.licenseDetails?.organisation}
  />
);

const LicenseKeyLine = ({ shouldShowViewLicenseLink, accountDetails }) => (
  <DetailsRow
    dataCy="action-section-license-key-line"
    label={<FormattedMessage id="mac.licenses.license.key" defaultMessage="License key" />}
    value={
      shouldShowViewLicenseLink ? (
        <StyledLink href={`/purchase/my/products/edit-server-id/${accountDetails.id}`}>
          <FormattedMessage id="mac.licenses.view.license" defaultMessage="View license" />
        </StyledLink>
      ) : (
        <StyledTextArea cols={33} rows={6} readOnly>
          {accountDetails?.latestPeriod.license?.licenseDetails?.license}
        </StyledTextArea>
      )
    }
  />
);

const restrictedKeys = [
  'pricingplan.technical-account-management',
  'pricingplan.premier-support',
  'priority-support.data-center',
  'bitbucket-priority-support.data-center',
  'priority-support.server',
  'enterprise-success-package',
  'bamboo-priority-support.data-center',
  'com.atlassian.advisory-services-essential-subscription',
  'com.atlassian.advisory-services-signature-subscription',
  'com.atlassian.advisory-services-elite-subscription',
  'com.atlassian.advisory-services-catalog-offering',
];

const DetailsRowContent = ({
  accountDetails,
  canUserSeeAccountDetails,
  endDate,
  billingContacts,
  technicalContacts,
  productKey,
  isAccount,
  isOpen,
  openModal,
  closeModal,
}) => {
  const productPackage = accountDetails.merchandise.productPackage || accountDetails.productPackage;
  const { key, productType, addOn, publiclyAvailable } = productPackage;
  const {
    merchandise: { details },
    latestPeriod,
  } = accountDetails;

  const keyIsAllowed = !restrictedKeys.includes(accountDetails.merchandise.productPackage.key);
  const isEvaluation = accountDetails.merchandise.licenseType === LICENSE_EVALUATION;
  const isOnDemand = accountDetails.merchandise.productPackage.onDemand;
  const isEntSubMigrated = accountDetails.entSubMigrated;

  const showContacts = keyIsAllowed && (!isEvaluation || isOnDemand) && !isEntSubMigrated;

  const dispatch = useDispatch();
  const isTrackedElsewhere: boolean = useSelector((state) =>
    selectTrackedElsewhere(state as State, accountDetails.id),
  );

  const accountHistory: any = useSelector((state) =>
    selectAccountHistory(state as State, accountDetails.id),
  );

  useEffect(() => {
    accountHistory?.periods.forEach((period) => {
      if (period.partnerId) dispatch(fetchPartnerDetails(period.partnerId));
    });
  }, []);

  const isNonPubliclyAvailableAddOn = addOn && !publiclyAvailable;

  const isBTFProduct = BTFProductTypes.includes(productType);

  const isNotSupportOnlyProduct =
    !isNoLicenseKeyValueDisplayedForKeys(key) &&
    !isAdvisoryServicesProduct(key) &&
    isCommercialOrAcademicOrDeveloperType(details.licenseType);

  const shouldShowViewLicenseLink =
    serverIdRequiredForProductTypes.includes(productType) &&
    !addOnProductTypes.includes(productType) &&
    !serverIdNotRequiredForKeys.includes(key);

  const accountWithoutHistory = [...atlassianCloudKeys, ATLASSIAN_ORGANISATION];
  const showHistory =
    !accountWithoutHistory.includes(productKey) && accountDetails?.periods?.length > 1;

  const shouldShowAccountURL =
    productKey !== ATLASSIAN_ORGANISATION &&
    accountDetails &&
    (accountDetails.accountUrl || accountDetails.isUnclaimedCloudSite);

  const isAccountHosted = hostedAccountTypes.includes(
    accountDetails?.merchandise?.productPackage?.productType,
  );

  const accountURLValue =
    accountDetails && accountDetails.accountUrl === '' && accountDetails.isUnclaimedCloudSite ? (
      <FormattedMessage id="mac.licenses.site.not.claimed" defaultMessage="Site not yet claimed" />
    ) : (
      accountDetails.accountUrl
    );

  const shouldShowAddressDetails =
    !accountDetails?.txAMigrated &&
    (productKey === 'atlassian.cloud' || productKey === ATLASSIAN_ORGANISATION);

  const showAutomaticallyChargedCardMessage =
    !atlassianCloudKeys.includes(productKey) &&
    accountDetails?.renewalAction === 'AUTO_RENEW' &&
    accountDetails?.creditCardData;

  const showContactUsToRenewMessage =
    !atlassianCloudKeys.includes(productKey) && accountDetails?.renewalAction !== 'AUTO_RENEW';

  const showContactUsForRenewal = new Date(accountDetails?.latestPeriod?.endDate) > new Date();

  return canUserSeeAccountDetails ? (
    <>
      <StyledTableData $paddingLeft colSpan={3}>
        <StyledTDSection>
          <StyledSenInfoWrapper>
            <DetailsRow
              label={<FormattedMessage {...messages.name} />}
              value={accountDetails?.name}
            />
            <DetailsRow
              label={<FormattedMessage {...messages.SEN} />}
              value={accountDetails?.id ? `SEN-${accountDetails.id}` : ''}
              dataCy="mac-sen-details"
            />
            {shouldShowAccountURL && (
              <DetailsRow label={<FormattedMessage {...messages.URL} />} value={accountURLValue} />
            )}
            {accountDetails && isAccountHosted && (
              <HostedAccountsDetails accountDetails={accountDetails} productKey={productKey} />
            )}
            {shouldShowAddressDetails && (
              <DetailsRow
                label={<FormattedMessage {...messages.addressDetails} />}
                value={
                  <StyledLink
                    href={`${config.adminHubUrl}/${
                      productKey !== ATLASSIAN_ORGANISATION ? 's' : 'o'
                    }/${accountDetails?.externalUuid}/billing/paymentdetails`}
                  >
                    <FormattedMessage {...messages.updateAddressDetails} />
                  </StyledLink>
                }
                dataCy="mac-address-details"
              />
            )}
            {showAutomaticallyChargedCardMessage && (
              <>
                <br />
                <SectionMessage>
                  <FormattedMessage
                    {...messages.automaticallyChargedCardMessage}
                    values={{
                      endDate: (
                        <FormattedDate
                          value={new Date(accountDetails?.latestPeriod?.endDate)}
                          day="2-digit"
                          month="long"
                          year="numeric"
                        />
                      ),
                    }}
                  />
                </SectionMessage>
              </>
            )}
            <br />
            {showContactUsToRenewMessage && (
              <StyledFlex width={`${Dimensions.units(12.5)}%`}>
                <SectionMessage>
                  <FormattedMessage {...messages.notAutomaticallyRenewedProduct} />
                  {showContactUsForRenewal && (
                    <FormattedMessage
                      {...messages.contactToRenew}
                      values={{
                        contactLink: (
                          <a href={config.companyContact}>
                            <FormattedMessage {...messages.contactUs} />
                          </a>
                        ),
                      }}
                    />
                  )}
                </SectionMessage>
              </StyledFlex>
            )}
          </StyledSenInfoWrapper>
          {endDate && (
            <SectionMessage appearance="information">
              <FormattedMessage
                {...messages.updateEndDateMessage}
                values={{
                  endDate: (
                    <FormattedDate
                      value={new Date(endDate)}
                      day="2-digit"
                      month="long"
                      year="numeric"
                    />
                  ),
                }}
              />
            </SectionMessage>
          )}
          <ContactsSection
            technicalContacts={technicalContacts}
            billingContacts={billingContacts}
            showContacts={showContacts}
            accountId={accountDetails.id}
            isOpen={isOpen}
            openModal={openModal}
            closeModal={closeModal}
          />
        </StyledTDSection>
      </StyledTableData>
      <StyledTableData colSpan={2}>
        {isTrackedElsewhere === false && (
          <>
            {isNonPubliclyAvailableAddOn && (
              <SectionMessage>
                <NonPubliclyAvailableAddOnMessage accountDetails={accountDetails} />
              </SectionMessage>
            )}
            <StyledTDSection>
              {isBTFProduct && (
                <>
                  <OrganizationLine latestPeriod={latestPeriod} />
                  {isNotSupportOnlyProduct && (
                    <LicenseKeyLine
                      shouldShowViewLicenseLink={shouldShowViewLicenseLink}
                      accountDetails={accountDetails}
                    />
                  )}
                </>
              )}
              <ActionsSection isAccount={isAccount} accountDetails={accountDetails} />
              {showHistory && (
                <StyledFlex>
                  <StyledTextLabel width={`${Dimensions.units(3.4)}%`}>
                    <FormattedMessage {...messages.history} />
                  </StyledTextLabel>
                  <AccountHistory accountHistory={accountHistory} />
                </StyledFlex>
              )}
            </StyledTDSection>
          </>
        )}
      </StyledTableData>
    </>
  ) : (
    // Show the proper error message once we have it: https://hello.jira.atlassian.cloud/browse/CHT-2819
    <StyledTableData $paddingLeft colSpan={5}>
      <p>You don&apos;t have permissions to see the details</p>
    </StyledTableData>
  );
};

export default DetailsRowContent;
